import {Dimension} from '../../components/layout/abstract-units';
import {CellId, getNewCellId} from '../../components/layout/cell';
import {CellDefinition, EdgeDefinition, LayoutDefinition} from '../../components/layout/layout-definition';
import {Widget} from '../../components/layout/widget/widget';
import {WidgetDefinition} from '../../components/layout/widget/widget-definition';

export type SplitLayoutWidgetDef = Pick<WidgetDefinition, 'widgetTypeId'>
    & Partial<Pick<WidgetDefinition, 'settings' | 'linkingGroupId'>>;

type SplitLayoutArgSingle = string | SplitLayoutWidgetDef;

type SplitLayoutArg = SplitLayoutArgSingle | SplitLayoutArgSingle[];

export function createCustomSplitLayoutDef(
    widgetsOrGroups: SplitLayoutArg[],
    edgesFactory: (
        vars: {
            cellIds: CellId[];
            cellIdDict: Record<string, CellId>
            WIDTH: number;
            HEIGHT: number;
            HALF_WIDTH: number;
            HALF_HEIGHT: number;
            axis: {
                H: Dimension;
                V: Dimension;
            }
        }
    ) => EdgeDefinition[]
): LayoutDefinition {
    const WIDTH: number = 1912;
    const HEIGHT: number = 973;
    const HALF_WIDTH: number = Math.trunc(WIDTH / 2);
    const HALF_HEIGHT: number = Math.trunc(HEIGHT / 2);

    const cellIds: CellId[] = Array.from(
        {length: widgetsOrGroups.length},
        getNewCellId
    );
    const cellIdDict: Record<string, CellId> = {};

    const cells: CellDefinition[] = [];
    const widgets: WidgetDefinition[] = [];

    const argToDef = (singleItem: SplitLayoutArgSingle): SplitLayoutWidgetDef =>
        typeof singleItem === 'string'
            ? {widgetTypeId: singleItem}
            : singleItem;

    widgetsOrGroups.forEach((itemOrItems: SplitLayoutArg, index: number) => {
        const cellWidgetDefs: SplitLayoutWidgetDef[] = Array.isArray(itemOrItems)
            ? itemOrItems.map(argToDef)
            : [argToDef(itemOrItems)];

        const cellId = cellIds[index];
        const cellName: string = cellWidgetDefs.map(def => def.widgetTypeId).join(',');

        cellIdDict[cellName] = cellId;

        const cellWidgets = cellWidgetDefs.map<WidgetDefinition>(def => ({
            id:             Widget.getNewWidgetId(),
            tabId:          null,
            cellId:         cellIds[index],
            widgetTypeId:   def.widgetTypeId,
            settings:       def.settings || null,
            linkingGroupId: def.linkingGroupId
        }));

        cells.push({
            id: cellIds[index],
            activeWidgetIndex: 0,
            widgets: cellWidgets.map(widget => widget.id)
        });
        widgets.push(...cellWidgets);
    });

    const edges: EdgeDefinition[] = edgesFactory({
        cellIds,
        cellIdDict,
        WIDTH, HEIGHT, HALF_WIDTH, HALF_HEIGHT,
        axis: {
            H: Dimension.X,
            V: Dimension.Y
        }
    });

    return {
        size: [
            WIDTH,
            HEIGHT
        ],
        tabId: null,
        cells,
        edges,
        widgets
    };
}

/**
 * Supports maximum 4 widgetTypeIds
 * @param widgetsOrGroups
 */
export function createSplitLayoutDef(...widgetsOrGroups: SplitLayoutArg[]): LayoutDefinition {
    return createCustomSplitLayoutDef(
        widgetsOrGroups,
        ({cellIds, WIDTH, HEIGHT, HALF_WIDTH, HALF_HEIGHT}) => {
            let edges: EdgeDefinition[];

            switch (cellIds.length) {
                case 1: {
                    edges = [];
                    break;
                }

                case 2: {
                    edges = [
                        {
                            axis:          1,
                            movingAxisPos: HALF_WIDTH,
                            axisRange:     [0, HEIGHT],
                            sides:         [[cellIds[1]], [cellIds[0]]]
                        }
                    ];
                    break;
                }

                case 3: {
                    edges = [
                        {
                            axis:          1,
                            movingAxisPos: HALF_WIDTH,
                            axisRange:     [0, HALF_HEIGHT],
                            sides:         [[cellIds[1]], [cellIds[0]]]
                        },
                        {
                            axis:          0,
                            movingAxisPos: HALF_HEIGHT,
                            axisRange:     [0, WIDTH],
                            sides:         [[cellIds[2]], [cellIds[1], cellIds[0]]]
                        }
                    ];
                    break;
                }

                case 4: {
                    edges = [
                        {
                            axis:          1,
                            movingAxisPos: HALF_WIDTH,
                            axisRange:     [0, HALF_HEIGHT],
                            sides:         [[cellIds[1]], [cellIds[0]]]
                        },
                        {
                            axis:          1,
                            movingAxisPos: HALF_WIDTH,
                            axisRange:     [HALF_HEIGHT, HEIGHT],
                            sides:         [[cellIds[3]], [cellIds[2]]]
                        },
                        {
                            axis:          0,
                            movingAxisPos: HALF_HEIGHT,
                            axisRange:     [0, HALF_WIDTH],
                            sides:         [[cellIds[2]], [cellIds[0]]]
                        },
                        {
                            axis:          0,
                            movingAxisPos: HALF_HEIGHT,
                            axisRange:     [HALF_WIDTH, WIDTH],
                            sides:         [[cellIds[3]], [cellIds[1]]]
                        }
                    ];
                    break;
                }
            }

            return edges;
        }
    );
}

export function createHorizontalSplitLayoutDef(
    ...widgetsOrGroups: [SplitLayoutArg, SplitLayoutArg]
): LayoutDefinition {
    return createCustomSplitLayoutDef(
        widgetsOrGroups,
        v => [{
            axis:          0,
            movingAxisPos: v.HALF_HEIGHT,
            axisRange:     [0, v.WIDTH],
            sides:         [[v.cellIds[1]], [v.cellIds[0]]]
        }]
    );
}
